// src/components/home/cardMagicReveal.js
//
// Vanilla JS + CSS "magic" show for CardShowcase's 10-card deck — no
// animation library, no Three.js. Runs as a self-contained looping
// sequence in its own overlay layer, ON TOP of the real, always-present,
// always-functional flex/scroll card strip underneath. The overlay owns
// its own lightweight <img> clones (not the real interactive cards), so
// nothing about this show can ever break the real strip's scroll/arrow/
// hover-tilt behavior — the two layers only ever hand off via opacity.
//
// The loop:
//   MORPH   — a single "stage" position cycles through all 10 card faces
//              one at a time, each swap covered by a rotating sparkle burst.
//   EXPAND  — the 10 cards fly out from that stage position to their
//              resting left-to-right slots, staggered, sparkle trailing.
//   IDLE    — cards settle into a gentle continuous 3D sway with occasional
//              sparkle glints. The REAL strip is faded in and takes over
//              here — fully interactive (scroll, arrows, hover-tilt).
//              The loop will not leave IDLE while the user is interacting.
//   COLLAPSE— cards fly back from their slots into the stage position.
//   VANISH  — a final covering sparkle burst + spin, then everything
//              fades to nothing, leaving a brief lingering sparkle glimpse.
//   (loops back to MORPH, forever)

const CORAL = '#FF6A4D'; // matches CustomCursor.jsx's cursor-trail coral exactly

const STYLE_TAG_ID = 'card-magic-reveal-styles';

const MORPH_STEP_MS = 620;
const EXPAND_STAGGER_MS = 110;
const EXPAND_SETTLE_MS = 750;
const COLLAPSE_STAGGER_MS = 100;
const COLLAPSE_SETTLE_MS = 650;
const VANISH_MS = 650;
const VANISH_EXPLODE_GAP_MS = 220;
const VANISH_GLIMPSE_MS = 900;
const IDLE_MIN_HOLD_MS = 6000;
const IDLE_INACTIVITY_REQUIRED_MS = 3000;
const IDLE_POLL_MS = 900;

function ensureStylesInjected() {
  if (document.getElementById(STYLE_TAG_ID)) return;
  const style = document.createElement('style');
  style.id = STYLE_TAG_ID;
  style.textContent = `
    .card-magic-overlay {
      position: absolute; inset: 0; perspective: 1000px;
      transition: opacity 0.5s ease;
    }
    .card-magic-slot {
      position: absolute; top: 0; left: 50%; width: var(--slot-w, 210px);
      transform-style: preserve-3d; pointer-events: none;
      transform:
        translateX(-50%)
        translateX(var(--slot-x, 0px))
        scale(var(--slot-scale, 0))
        rotateY(var(--slot-rot, 0deg));
      opacity: var(--slot-opacity, 0);
      transition:
        transform var(--slot-duration, 0.7s) cubic-bezier(0.22, 1, 0.36, 1),
        opacity var(--slot-duration, 0.7s) ease;
    }
    .card-magic-slot img {
      display: block; width: 100%; border-radius: 12px;
      border: 4px solid rgba(251,248,244,0.92);
      box-shadow: 0 20px 40px -18px rgba(0,0,0,0.6);
    }
    .card-magic-sway {
      animation: card-magic-sway-kf 4.2s ease-in-out infinite;
    }
    @keyframes card-magic-sway-kf {
      0%, 100% { transform: translateX(-50%) translateX(var(--slot-x, 0px)) scale(1) rotateY(-3deg) rotateX(1deg); }
      50% { transform: translateX(-50%) translateX(var(--slot-x, 0px)) scale(1) rotateY(3deg) rotateX(-1deg); }
    }
    .card-sparkle {
      position: absolute; left: 50%; top: 50%; border-radius: 50%;
      pointer-events: none; opacity: 0;
    }
    .card-sparkle--explode {
      animation: card-sparkle-explode-kf 650ms ease-out forwards;
    }
    @keyframes card-sparkle-explode-kf {
      0% { opacity: 0; transform: translate(-50%, -50%) scale(0.3); }
      30% { opacity: 1; }
      100% { opacity: 0; transform: var(--sparkle-end, translate(-50%, -50%)) scale(0.25); }
    }
    .card-sparkle--implode {
      animation: card-sparkle-implode-kf 550ms ease-in forwards;
    }
    @keyframes card-sparkle-implode-kf {
      0% { opacity: 0; transform: var(--sparkle-start, translate(-50%, -50%)) scale(1); }
      35% { opacity: 1; }
      100% { opacity: 0; transform: translate(-50%, -50%) scale(0.1); }
    }
    .card-sparkle--heartbeat {
      animation: card-sparkle-heartbeat-kf 900ms ease-in-out forwards;
    }
    @keyframes card-sparkle-heartbeat-kf {
      0% { opacity: 0; transform: translate(-50%, -50%) scale(0.5); }
      12% { opacity: 1; transform: translate(-50%, -50%) scale(1.15); }
      24% { opacity: 0.55; transform: translate(-50%, -50%) scale(0.7); }
      40% { opacity: 1; transform: translate(-50%, -50%) scale(1.25); }
      55% { opacity: 0.5; transform: translate(-50%, -50%) scale(0.65); }
      100% { opacity: 0; transform: translate(-50%, -50%) scale(0.2); }
    }
    @media (prefers-reduced-motion: reduce) {
      .card-magic-overlay { display: none; }
      .card-magic-sway { animation: none; }
    }
  `;
  document.head.appendChild(style);
}

function prefersReducedMotion() {
  return typeof window.matchMedia === 'function'
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function wait(ms) { return new Promise((resolve) => { window.setTimeout(resolve, ms); }); }

// Forces layout so a style write actually commits before a later write in
// the same tick — otherwise the browser collapses "reset then animate to"
// into one paint and the transition never plays (same fix CustomCursor.jsx
// uses via getBoundingClientRect()).
function forceReflow(el) { return el.offsetHeight; }

// `centerOffsetX`: bursts default to the layer's own center (used for the
// single-stage MORPH/VANISH phases). EXPAND/COLLAPSE/idle-glint bursts pass
// each card's own resting x-offset so the sparkle appears at that card's
// slot rather than always at the strip's overall center.
// `mode`: 'explode' (default) drifts outward from center and fades — used
// for arrivals/reveals. 'implode' starts at the outer radius and is sucked
// inward — used for the vanish-phase implosion.
function spawnSparkles(layerEl, color, count, coverRadius, centerOffsetX = 0, mode = 'explode') {
  if (!layerEl || prefersReducedMotion()) return;
  const rect = layerEl.getBoundingClientRect();
  const radiusBase = coverRadius || Math.max(rect.width, rect.height) * 0.42;

  for (let k = 0; k < count; k++) {
    const angle = (k / count) * Math.PI * 2 + (Math.random() - 0.5) * 0.7;
    const radius = radiusBase + Math.random() * radiusBase * 0.4;
    const dx = Math.cos(angle) * radius;
    const dy = Math.sin(angle) * radius;
    const size = 6 + Math.random() * 6;

    const el = document.createElement('i');
    el.className = `card-sparkle card-sparkle--${mode}`;
    el.style.width = `${size}px`;
    el.style.height = `${size}px`;
    el.style.background = color;
    el.style.boxShadow = `0 0 ${size * 2}px ${size * 0.6}px ${color}`;
    if (centerOffsetX) el.style.left = `calc(50% + ${centerOffsetX}px)`;
    const offset = `translate(calc(-50% + ${dx}px), calc(-50% + ${dy}px))`;
    el.style.setProperty(mode === 'implode' ? '--sparkle-start' : '--sparkle-end', offset);
    el.addEventListener('animationend', () => el.remove(), { once: true });
    window.setTimeout(() => el.remove(), 1000);
    layerEl.appendChild(el);
  }
}

// A small cluster that pulses twice like a heartbeat before fading — used
// as the "something is being born" glimpse between one loop's vanish and
// the next loop's first reveal.
function spawnHeartbeatGlimpse(layerEl, color, count) {
  if (!layerEl || prefersReducedMotion()) return;
  for (let k = 0; k < count; k++) {
    const dx = (Math.random() - 0.5) * 30;
    const dy = (Math.random() - 0.5) * 30;
    const size = 7 + Math.random() * 5;

    const el = document.createElement('i');
    el.className = 'card-sparkle card-sparkle--heartbeat';
    el.style.width = `${size}px`;
    el.style.height = `${size}px`;
    el.style.background = color;
    el.style.boxShadow = `0 0 ${size * 2}px ${size * 0.6}px ${color}`;
    el.style.left = `calc(50% + ${dx}px)`;
    el.style.top = `calc(50% + ${dy}px)`;
    el.addEventListener('animationend', () => el.remove(), { once: true });
    window.setTimeout(() => el.remove(), 1100);
    layerEl.appendChild(el);
  }
}

// Cards visibly drag a few sparkles along as they fly from `fromX` to
// `toX` (EXPAND/COLLAPSE), instead of only bursting once at the start.
function trailAlongMove(layerEl, color, fromX, toX, durationMs, isStopped) {
  const steps = 4;
  for (let s = 1; s <= steps; s++) {
    const t = s / (steps + 1);
    const x = fromX + (toX - fromX) * t;
    window.setTimeout(() => {
      if (isStopped() || !layerEl) return;
      spawnSparkles(layerEl, color, 2, 12, x, 'explode');
    }, durationMs * t);
  }
}

// One run of the full loop. Returns a `stop()` function to tear it down
// (component unmount). All DOM reads/writes are guarded by a `stopped`
// flag so an in-flight `await wait(...)` can't touch an unmounted page.
export function startCardMagicShow({ overlayEl, sparkleLayerEl, images, cardWidth, cardGap, realStripEl, activityTracker }) {
  ensureStylesInjected();
  if (prefersReducedMotion()) {
    // No show at all — just make sure the real strip is visible and
    // interactive from the start.
    if (realStripEl) realStripEl.style.opacity = '1';
    if (realStripEl) realStripEl.style.pointerEvents = 'auto';
    return () => {};
  }

  let stopped = false;
  const count = images.length;
  const slots = images.map((src) => {
    const el = document.createElement('div');
    el.className = 'card-magic-slot';
    el.style.setProperty('--slot-w', `${cardWidth}px`);
    const img = document.createElement('img');
    img.src = src;
    img.alt = '';
    el.appendChild(img);
    overlayEl.appendChild(el);
    return el;
  });

  const setSlot = (el, { x, scale, rot, opacity, durationMs }) => {
    if (x !== undefined) el.style.setProperty('--slot-x', `${x}px`);
    if (scale !== undefined) el.style.setProperty('--slot-scale', String(scale));
    if (rot !== undefined) el.style.setProperty('--slot-rot', `${rot}deg`);
    if (opacity !== undefined) el.style.setProperty('--slot-opacity', String(opacity));
    if (durationMs !== undefined) el.style.setProperty('--slot-duration', `${durationMs}ms`);
  };

  const restingX = (i) => (i - (count - 1) / 2) * (cardWidth + cardGap);
  const isStopped = () => stopped;

  const showRealStrip = (visible) => {
    if (!realStripEl) return;
    realStripEl.style.transition = 'opacity 0.5s ease';
    realStripEl.style.opacity = visible ? '1' : '0';
    realStripEl.style.pointerEvents = visible ? 'auto' : 'none';
  };

  async function morphPhase() {
    // Start with card 0 sitting centered, hidden behind a sparkle burst
    // that then clears to reveal it.
    slots.forEach((el, i) => setSlot(el, { x: 0, scale: 0, rot: 0, opacity: 0, durationMs: 1 }));
    forceReflow(slots[0]);
    setSlot(slots[0], { scale: 1, rot: 0, opacity: 1, durationMs: 450 });
    spawnSparkles(sparkleLayerEl, CORAL, 8);
    await wait(MORPH_STEP_MS);

    for (let i = 1; i < count; i++) {
      if (stopped) return;
      // Current card rotates away under a covering sparkle burst...
      setSlot(slots[i - 1], { rot: 95, opacity: 0, durationMs: 320 });
      spawnSparkles(sparkleLayerEl, CORAL, 9);
      await wait(320);
      if (stopped) return;
      // ...then the next card rotates in from the opposite side.
      setSlot(slots[i], { x: 0, scale: 1, rot: -95, opacity: 0, durationMs: 1 });
      forceReflow(slots[i]);
      setSlot(slots[i], { rot: 0, opacity: 1, durationMs: 420 });
      await wait(MORPH_STEP_MS - 320);
    }
  }

  async function expandPhase() {
    for (let i = 0; i < count; i++) {
      if (stopped) return;
      setSlot(slots[i], { x: restingX(i), scale: 1, rot: 0, opacity: 1, durationMs: EXPAND_SETTLE_MS });
      spawnSparkles(sparkleLayerEl, CORAL, 6, cardWidth * 0.3, restingX(i));
      trailAlongMove(sparkleLayerEl, CORAL, 0, restingX(i), EXPAND_SETTLE_MS, isStopped);
      await wait(EXPAND_STAGGER_MS);
    }
    await wait(EXPAND_SETTLE_MS - EXPAND_STAGGER_MS);
  }

  async function idlePhase() {
    slots.forEach((el) => el.classList.add('card-magic-sway'));
    // Hand off to the real, fully-interactive strip; the overlay stays
    // mounted (sways gently, very low opacity would be redundant so we
    // just hide it) but real content underneath is what the user sees
    // and touches from here on.
    overlayEl.style.opacity = '0';
    showRealStrip(true);

    const holdStart = Date.now();
    // Occasional idle sparkle glints while the real strip is live —
    // subtle, infrequent, never covering the actual cards.
    const glintTimer = window.setInterval(() => {
      if (stopped) return;
      const i = Math.floor(Math.random() * count);
      spawnSparkles(sparkleLayerEl, CORAL, 2, cardWidth * 0.5, restingX(i));
    }, 2200);

    try {
      // Always hold at least IDLE_MIN_HOLD_MS, then keep waiting for a
      // quiet stretch (no interaction for IDLE_INACTIVITY_REQUIRED_MS)
      // before ever proceeding to collapse.
      while (!stopped) {
        const elapsed = Date.now() - holdStart;
        const quietFor = activityTracker ? Date.now() - activityTracker.lastActivity() : Infinity;
        if (elapsed > IDLE_MIN_HOLD_MS && quietFor > IDLE_INACTIVITY_REQUIRED_MS) break;
        // eslint-disable-next-line no-await-in-loop
        await wait(IDLE_POLL_MS);
      }
    } finally {
      window.clearInterval(glintTimer);
    }

    if (stopped) return;
    showRealStrip(false);
    overlayEl.style.opacity = '1';
    slots.forEach((el) => el.classList.remove('card-magic-sway'));
    // Re-apply the resting transform explicitly (the sway class's own
    // keyframe was driving the transform while active; without this the
    // element could be caught mid-keyframe when the class is removed).
    slots.forEach((el, i) => setSlot(el, { x: restingX(i), scale: 1, rot: 0, opacity: 1, durationMs: 1 }));
  }

  async function collapsePhase() {
    for (let i = count - 1; i >= 0; i--) {
      if (stopped) return;
      setSlot(slots[i], { x: 0, scale: 0.85, rot: 0, opacity: 0.9, durationMs: COLLAPSE_SETTLE_MS });
      spawnSparkles(sparkleLayerEl, CORAL, 6, cardWidth * 0.3, restingX(i));
      trailAlongMove(sparkleLayerEl, CORAL, restingX(i), 0, COLLAPSE_SETTLE_MS, isStopped);
      await wait(COLLAPSE_STAGGER_MS);
    }
    await wait(COLLAPSE_SETTLE_MS - COLLAPSE_STAGGER_MS);
  }

  async function vanishPhase() {
    // Implosion: sparkles get sucked inward as the cards themselves
    // collapse hard toward nothing — reads as one thing imploding, not a
    // plain fade.
    spawnSparkles(sparkleLayerEl, CORAL, 14, cardWidth * 0.55, 0, 'implode');
    slots.forEach((el) => setSlot(el, { scale: 0.05, rot: 220, opacity: 0, durationMs: VANISH_MS }));
    await wait(VANISH_MS);
    if (stopped) return;

    // Explosion: the hinge moment between the loop that just ended and the
    // one about to begin.
    spawnSparkles(sparkleLayerEl, CORAL, 18, cardWidth * 0.65, 0, 'explode');
    await wait(VANISH_EXPLODE_GAP_MS);
    if (stopped) return;

    // A pulsing "heartbeat" cluster lingers in the empty space — something
    // about to be born — before the loop restarts on the next card.
    spawnHeartbeatGlimpse(sparkleLayerEl, CORAL, 6);
    await wait(VANISH_GLIMPSE_MS);
  }

  // Real strip stays visible/interactive by default (safe fallback) until
  // the show actually takes over for its first MORPH phase.
  overlayEl.style.opacity = '1';
  showRealStrip(false);

  (async function loop() {
    while (!stopped) {
      // eslint-disable-next-line no-await-in-loop
      await morphPhase();
      if (stopped) break;
      // eslint-disable-next-line no-await-in-loop
      await expandPhase();
      if (stopped) break;
      // eslint-disable-next-line no-await-in-loop
      await idlePhase();
      if (stopped) break;
      // eslint-disable-next-line no-await-in-loop
      await collapsePhase();
      if (stopped) break;
      // eslint-disable-next-line no-await-in-loop
      await vanishPhase();
    }
  }());

  return function stop() {
    stopped = true;
    slots.forEach((el) => el.remove());
    if (realStripEl) {
      realStripEl.style.opacity = '1';
      realStripEl.style.pointerEvents = 'auto';
    }
  };
}

// Small helper: tracks "last interaction" across a set of DOM elements,
// exposing `.lastActivity()` for startCardMagicShow's idle-phase polling.
// Interaction = pointer entering/moving over the strip, or a scroll/click
// on it — anything that means "the user is looking at these cards."
export function trackInteraction(targetEls) {
  let last = 0;
  const mark = () => { last = Date.now(); };
  mark(); // don't collapse immediately on mount before any real idle time passes
  const events = ['pointerenter', 'pointermove', 'scroll', 'click'];
  targetEls.forEach((el) => {
    if (!el) return;
    events.forEach((evt) => el.addEventListener(evt, mark, { passive: true }));
  });
  return {
    lastActivity: () => last,
    destroy: () => {
      targetEls.forEach((el) => {
        if (!el) return;
        events.forEach((evt) => el.removeEventListener(evt, mark));
      });
    },
  };
}
